/**
 * Замечания контроля качества в «Чатах» и ченджлог базы знаний — `/api/qc`.
 * Спека `docs/specs/2026-10-25-qc-remarks-design.md`.
 */
import { apiFetch, query } from "@/lib/api";
import { uploadVoice } from "@/lib/voice";

export type QcMark = "good" | "bad" | "note";
export type QcRefKind = "message" | "call";
export type QcRef = { kind: QcRefKind; id: number; chat_id: number };
export const refKey = (r: { kind: QcRefKind; id: number }) => `${r.kind === "message" ? "m" : "c"}:${r.id}`;

export const MARKS: { id: QcMark; label: string; title: string }[] = [
  { id: "note", label: "Замечание", title: "Нейтральное замечание" },
  { id: "bad", label: "Плохо", title: "Ошибка: так отвечать не надо" },
  { id: "good", label: "Хорошо", title: "Хороший пример: так и надо отвечать" },
];
export const markLabel = (m: QcMark) => MARKS.find((x) => x.id === m)?.label ?? m;

/** Заготовки фраз: кладутся в поле, контролёр дописывает своё. */
export const PHRASES = [
  "Неверное правило",
  "Не уточнили условие",
  "Не предложили запись",
  "Удачная формулировка",
  "В базе не хватает ответа",
];

export type QcContextItem = {
  kind: QcRefKind;
  id: number;
  chat_id: number;
  out: boolean;
  author: string;
  date: string | null;
  text: string;
  file?: string | null;
  duration?: number;
};

export type QcRemarkStatus =
  | "waiting_voice"
  | "voice_failed"
  | "new"
  | "in_changelog"
  | "applied"
  | "no_action"
  | "rejected";

export type QcRemark = {
  id: number;
  user_id: number;
  author: string;
  chat_id: number;
  mark: QcMark;
  text: string;
  transcript: string;
  audio: boolean;
  status: QcRemarkStatus;
  status_text: string;
  error: string;
  date: string | null;
  refs: QcRef[];
  /** Снимок переписки; `null` — источник в закрытой для меня группе (`closed`). */
  /** `whole_chat` — ничего не отмечали: замечание ко всему чату, `before` — последние реплики. */
  context?: { items: QcContextItem[]; before: QcContextItem[]; whole_chat?: boolean } | null;
  closed?: boolean;
  editable: boolean;
  /** Пункт ченджлога; `card_id`/`card_title` — ЦЕЛЬ решения модели, ещё не итог. */
  item: {
    id: number;
    changelog_id: number;
    action: QcAction;
    title: string;
    why: string;
    status: QcItemStatus;
    revision: number;
    card_id: number | null;
    card_title: string;
  } | null;
  /** Итог: куда внесено (`applied`) или что подтвердило правило (`no_action`). */
  card: { id: number; title: string; version: number | null; current: number; search: QcSearch } | null;
};
export type QcSearch = "актуальна" | "ожидает обновления" | "ошибка";

/** Требует внимания контролёра: не расшифровалось, на доработке или ошибка модели. */
export const needsAttention = (r: QcRemark) =>
  r.status === "voice_failed" || (r.status === "in_changelog" && (r.item?.status === "rework" || r.item?.status === "failed"));
/** Ещё в работе (не решено): для цвета бейджа. */
export const inProgress = (r: QcRemark) => ["waiting_voice", "new", "in_changelog"].includes(r.status);

export const chatRemarksKey = (chatId: number) => ["qc-remarks", chatId] as const;
export const loadChatRemarks = (chatId: number) =>
  apiFetch<{ remarks: QcRemark[]; can_add: boolean }>(`/api/qc/chats/${chatId}/remarks`);

export const createRemark = (body: {
  chat_id: number;
  mark: QcMark;
  text: string;
  audio: string | null;
  refs: QcRef[];
  client_key: string;
}) => apiFetch<QcRemark>("/api/qc/remarks", { method: "POST", body: JSON.stringify(body) });

export const editRemark = (id: number, body: { text?: string; mark?: QcMark; transcript?: string }) =>
  apiFetch<QcRemark>(`/api/qc/remarks/${id}`, { method: "PATCH", body: JSON.stringify(body) });

export const deleteRemark = (id: number) => apiFetch<{ deleted: number }>(`/api/qc/remarks/${id}`, { method: "DELETE" });

export const audioUrl = (kind: "remark" | "comment", id: number) => `/api/qc/audio/${kind}/${id}`;

/** Запись → общий модуль голоса; токен QC — `v<id>` записи (сервер приложит её к замечанию). */
export const uploadAudio = async (blob: Blob, seconds = 0) => `v${(await uploadVoice(blob, seconds)).id}`;

export type QcAction = "edit" | "create" | "none";
export type QcItemStatus = "drafting" | "review" | "rework" | "approved" | "rejected" | "failed";
export type QcContent = { title: string; body: string; kind: string; topic: string; location_id: number | null };
export type QcComment = {
  id: number;
  item_id: number | null;
  author: string;
  user_id: number | null;
  text: string;
  transcript: string;
  audio: boolean;
  status: "waiting_voice" | "ready" | "voice_failed";
  revision: number;
  used: boolean;
  date: string;
};
export type QcCardBrief = {
  id: number;
  title: string;
  version: number;
  status: string;
  topic: string;
  location_id: number | null;
  search: "актуальна" | "ожидает обновления" | "ошибка";
};
export type QcItem = {
  id: number;
  changelog_id: number;
  action: QcAction;
  status: QcItemStatus;
  revision: number;
  title: string;
  why: string;
  change: string;
  error: string;
  history: {
    revision: number;
    action: QcAction;
    card_id: number | null;
    title: string;
    why: string;
    change: string;
    date: string;
    /** Комментарии, учтённые моделью при доработке этой ревизии. */
    comments?: number[];
  }[];
  decided_by: number | null;
  decided_at: string | null;
  card: QcCardBrief | null;
  result_version: number | null;
  topic: string;
  proposal: {
    id: number;
    status: string;
    base_version: number;
    reason: string;
    after: QcContent;
    before: QcContent | null;
  } | null;
  remarks: QcRemark[];
  comments: QcComment[];
};
export type QcChangelogStatus = "queued" | "building" | "review" | "done" | "empty" | "failed";
export type QcChangelogRow = {
  id: number;
  status: QcChangelogStatus;
  trigger: "manual" | "auto";
  user_id: number | null;
  summary: string;
  error: string;
  cost: number | null;
  remarks: number;
  missed: number;
  date: string | null;
  built: string | null;
  items: number;
  pending: number;
};
export type QcSummary = {
  new: number;
  waiting_voice: number;
  voice_failed: number;
  in_changelog: number;
  applied: number;
  items_review: number;
  items_failed: number;
  items_rework: number;
  active: { id: number; status: string; date: string } | null;
  /** Когда соберётся сама; null — авто-сборка выключена (только по кнопке). */
  next_auto: string | null;
};

export const loadSummary = () => apiFetch<QcSummary & { editor: boolean }>("/api/qc/summary");
export const loadChangelogs = () => apiFetch<{ changelogs: QcChangelogRow[]; summary: QcSummary }>("/api/qc/changelogs");
export const loadChangelog = (id: number) =>
  apiFetch<QcChangelogRow & { comments: QcComment[]; items_list: QcItem[]; cities: Cities }>(`/api/qc/changelogs/${id}`);
export const requestBuild = () => apiFetch<QcChangelogRow>("/api/qc/changelogs", { method: "POST" });

type Decision = { text?: string; audio?: string | null; revision?: number };
export type Cities = Record<string, string>;
const post = <T>(path: string, body: unknown) => apiFetch<T>(path, { method: "POST", body: JSON.stringify(body) });
export const approveItem = (id: number, body: Decision & { revision: number }) =>
  post<{ status: QcItemStatus }>(`/api/qc/items/${id}/approve`, body);
export const rejectItem = (id: number, body: Decision) => post<{ status: QcItemStatus }>(`/api/qc/items/${id}/reject`, body);
export const reworkItem = (id: number, body: Decision) => post<{ status: QcItemStatus }>(`/api/qc/items/${id}/rework`, body);
export const retryItem = (id: number) => post<{ status: QcItemStatus }>(`/api/qc/items/${id}/retry`, {});
export const commentItem = (id: number, body: Decision) => post<QcComment>(`/api/qc/items/${id}/comments`, body);
export const approveSelected = (changelogId: number, items: [number, number][]) =>
  post<{ approved: number[]; rework: number[]; errors: { id: number; error: string }[] }>(
    `/api/qc/changelogs/${changelogId}/approve`,
    { items },
  );
export const reworkAll = (changelogId: number, body: Decision) => post<{ rework: number }>(`/api/qc/changelogs/${changelogId}/rework`, body);
export const commentChangelog = (changelogId: number, body: Decision) =>
  post<QcComment>(`/api/qc/changelogs/${changelogId}/comments`, body);

export const loadRemarks = (p: { mine: boolean; status?: string; mark?: string; author?: number | null; from?: string; to?: string }) =>
  apiFetch<{ remarks: QcRemark[]; all_allowed: boolean; authors: { id: number; name: string; count: number }[] }>(
    `/api/qc/remarks${query({ mine: p.mine ? "true" : "false", status: p.status, mark: p.mark, author: p.author ?? undefined, from: p.from, to: p.to })}`,
  );

export type QcCardBases = {
  card: QcCardBrief & { body: string };
  versions: {
    number: number;
    author_kind: string;
    user_id: number | null;
    reason: string;
    proposal_id: number | null;
    date: string | null;
    content: QcContent;
    remarks: QcRemark[];
    confirmed: QcRemark[];
  }[];
};
export const loadCardBases = (id: number) => apiFetch<QcCardBases>(`/api/qc/cards/${id}`);

export const ACTION_LABEL: Record<QcAction, string> = { edit: "Правка карточки", create: "Новая статья", none: "База не меняется" };
export const ITEM_STATUS_LABEL: Record<QcItemStatus, string> = {
  drafting: "Модель пишет редакцию",
  review: "Ждёт решения",
  rework: "На доработке у модели",
  approved: "Утверждено",
  rejected: "Отклонено",
  failed: "Ошибка — нужна доработка",
};
